<!-- Task Header Container -->
<div class="task-header">

  <!-- Left Section: Title, Status, Project Info -->
  <div class="header-left">

    <!-- Title and Status Badge Row -->
    <div class="title-row">
      <h2 class="header-title">{{ taskTitle || (isEdit ? 'Edit Task' : 'New Task') }}</h2>
      @if (status) {
        <app-status-pill
          variant="solid"
          [color]="selectedStatus?.color"
          [label]="getFormattedStatus()"
          [locked]="getSelectedStatusLocked()"
          [truncateAt]="30">
        </app-status-pill>
      }
    </div>

    <!-- Project Info Row -->
    @if (projectName) {
      <div class="project-info">
        <span class="info-label">{{ moduleLabel }}:</span>
        @if (recordRoute) {
          <a class="info-value record-link" [routerLink]="recordRoute" (click)="recordLinkClick.emit()">{{ projectName }}</a>
          <span class="separator">•</span>
          <a class="project-id record-link" [routerLink]="recordRoute" (click)="recordLinkClick.emit()">#{{ projectId }}</a>
        } @else {
          <span class="info-value">{{ projectName }}</span>
          <span class="separator">•</span>
          <span class="project-id">#{{ projectId }}</span>
        }
        <!-- Milestone Indicator -->
        @if (milestone) {
          <span class="separator">•</span>
          <span class="milestone-wrapper">
            <mat-icon class="milestone-icon">flag</mat-icon>
            <span class="milestone-text">Milestone: {{ milestone.name }}</span>
          </span>
        }
      </div>
    }

  </div>

  <!-- Right Section: Action Buttons -->
  <div class="header-actions">

    <!-- GROUP 1: Content Actions (Templates, Stages) -->
    @if ((!isEdit && hasTemplates && templates.length > 0) || hasStages || showFlag) {
      <div class="action-group">
        <!-- Template Menu Button -->
        @if (!isEdit && hasTemplates && templates.length > 0) {
          <button
            mat-icon-button
            class="action-button"
            [matMenuTriggerFor]="templateMenu"
            [attr.aria-label]="'Load template'"
            matTooltip="Load from template">
            <mat-icon>article</mat-icon>
          </button>
        }
        <mat-menu #templateMenu="matMenu" xPosition="before">
          <div class="menu-header">
            <mat-icon class="menu-header-icon">article</mat-icon>
            <span class="menu-header-text">Task Templates</span>
          </div>
          <mat-divider></mat-divider>
          @for (template of templates; track template) {
            <button
              mat-menu-item
              (click)="onTemplateSelect(template)"
              class="template-menu-item">
              <mat-icon class="template-icon">insert_drive_file</mat-icon>
              <span class="template-name">{{ template.name || template.title }}</span>
            </button>
          }
          @if (templates.length === 0) {
            <mat-divider></mat-divider>
          }
          @if (templates.length === 0) {
            <div class="menu-empty-state">
              <mat-icon class="empty-icon">info</mat-icon>
              <span class="empty-text">No templates available</span>
            </div>
          }
        </mat-menu>
        <!-- Stages Dropdown Menu Button -->
        @if (hasStages) {
          <button
            mat-icon-button
            class="action-button"
            [matMenuTriggerFor]="stagesMenu"
            [disabled]="isLocked"
            [ngClass]="{'permission-disabled': isLocked}"
            [attr.aria-label]="'Select stage'"
            [matTooltip]="'Stage: ' + getStageDisplayName()">
            <mat-icon>layers</mat-icon>
          </button>
        }
        <!-- Flag: a click opens a popup with an optional reason; the task is only flagged / unflagged when Save is clicked -->
        @if (showFlag) {
          <button
            mat-icon-button
            class="action-button flag-button"
            [class.flag-active]="flagged"
            #flagTrigger="matMenuTrigger"
            [matMenuTriggerFor]="flagMenu"
            (click)="onFlagClick()"
            (menuOpened)="onFlagMenuOpened()"
            [disabled]="isLocked"
            [ngClass]="{'permission-disabled': isLocked}"
            [attr.aria-label]="flagged ? 'Remove flag' : 'Flag this task'"
            [matTooltip]="flagged ? (flagText || 'Flagged (click to remove)') : 'Flag this task'"
            matTooltipClass="task-flag-tooltip">
            <mat-icon>{{ flagged ? 'flag' : 'outlined_flag' }}</mat-icon>
          </button>
        }
        <mat-menu #flagMenu="matMenu" xPosition="after" class="task-flag-menu">
          <div class="flag-popup" (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()">
            <div class="flag-popup__title"><mat-icon>flag</mat-icon>{{ flagPopupUnflagged ? 'Unflag Reason (optional)' : 'Flag Reason (optional)' }}</div>
            <textarea
              class="flag-popup__input"
              rows="4"
              maxlength="1000"
              [(ngModel)]="flagDraft"
              [placeholder]="flagPopupUnflagged ? 'Why is the flag being removed?' : 'Why is this task being flagged?'"></textarea>
            <div class="flag-popup__actions">
              <span class="flag-popup__spacer"></span>
              <button type="button" class="flag-popup__cancel" (click)="flagTrigger?.closeMenu()">Cancel</button>
              <button type="button" class="flag-popup__save" (click)="saveFlag()">Save</button>
            </div>
          </div>
        </mat-menu>
        <mat-menu #stagesMenu="matMenu" xPosition="before">
          <div class="menu-header">
            <mat-icon class="menu-header-icon">layers</mat-icon>
            <span class="menu-header-text">Task Stage</span>
          </div>
          <mat-divider></mat-divider>
          @for (stage of stages; track stage) {
            <button
              mat-menu-item
              (click)="onStageSelect(stage)"
              [class.stage-menu-item-selected]="selectedStage?.id === stage.id"
              class="stage-menu-item">
              <mat-icon class="stage-icon" [style.color]="stage.color || '#6b7280'">
                {{ selectedStage?.id === stage.id ? 'check_circle' : 'radio_button_unchecked' }}
              </mat-icon>
              <span class="stage-name">{{ stage.name }}</span>
              @if (stage.description) {
                <span class="stage-description">{{ stage.description }}</span>
              }
            </button>
          }
          @if (stages.length === 0) {
            <mat-divider></mat-divider>
          }
          @if (stages.length === 0) {
            <div class="menu-empty-state">
              <mat-icon class="empty-icon">info</mat-icon>
              <span class="empty-text">Loading stages...</span>
            </div>
          }
        </mat-menu>
      </div>
    }

    <!-- Divider after content actions -->
    @if ((!isEdit && hasTemplates && templates.length > 0) || (hasStages && stages.length > 0)) {
      <div class="action-divider"></div>
    }

    <!-- GROUP 2: Task Properties (Priority) -->
    <div class="action-group">
      <!-- High Priority Toggle Button -->
      <button
        mat-icon-button
        class="action-button priority-toggle-button"
        [class.priority-active]="isHighPriority"
        (click)="onPriorityToggle()"
        [attr.aria-label]="isHighPriority ? 'Remove high priority' : 'Set high priority'"
        [matTooltip]="isHighPriority ? 'High Priority (Click to remove)' : 'Set High Priority'">
        <mat-icon>local_fire_department</mat-icon>
      </button>
    </div>

    <!-- Divider after priority -->
    <div class="action-divider"></div>

    <!-- GROUP 3: View Mode Actions (Status, Mark Complete, PDF, Lock) -->
    @if (isViewMode && (showStatusDropdown || showMarkComplete || showPdfExport || showCopy)) {
      <div class="action-group">
        <!-- View Mode: Status Dropdown Button -->
        <!-- <button
        *ngIf="showStatusDropdown && hasStatuses"
        mat-icon-button
        class="action-button action-button--info"
        [matMenuTriggerFor]="statusMenu"
        [appPermission]="statusChangePermission"
        [attr.aria-label]="'Change status'"
        matTooltip="Change Status">
        <mat-icon>change_circle</mat-icon>
      </button>
      <mat-menu #statusMenu="matMenu" xPosition="before">
        <div class="menu-header">
          <mat-icon class="menu-header-icon">change_circle</mat-icon>
          <span class="menu-header-text">Change Status</span>
        </div>
        <mat-divider></mat-divider>
        <button
          mat-menu-item
          *ngFor="let status of statuses"
          (click)="onStatusSelect(status)"
          [class.stage-menu-item-selected]="selectedStatus?.id === status.id"
          class="stage-menu-item">
          <mat-icon class="stage-icon">
            {{ selectedStatus?.id === status.id ? 'check_circle' : 'radio_button_unchecked' }}
          </mat-icon>
          <span class="stage-name">{{ status.name }}</span>
        </button>
      </mat-menu> -->
      <!-- View Mode: Lock/Unlock Button -->
      <button
        mat-icon-button
        class="action-button lock-toggle-button"
        [class.locked]="isLocked"
        (click)="onLockToggle()"
        appPermission="TASK-LOCKUNLOCK"
        [attr.aria-label]="isLocked ? 'Unlock task' : 'Lock task'"
        [matTooltip]="isLocked ? 'Task Locked - Click to Unlock' : 'Lock Task'">
        <mat-icon>{{ isLocked ? 'lock' : 'lock_open' }}</mat-icon>
      </button>
      <!-- View Mode: Mark Complete Button -->
      @if (showMarkComplete) {
        <button
          mat-icon-button
          class="action-button action-button--success"
          [appPermission]="markCompletePermission"
          [disabled]="isLocked"
          [ngClass]="{'permission-disabled': isLocked}"
          (click)="onMarkComplete()"
          [attr.aria-label]="'Mark complete'"
          matTooltip="Mark Complete">
          <mat-icon>check_circle</mat-icon>
        </button>
      }
      <!-- View Mode: Copy Button -->
      @if (showCopy) {
        <button
          mat-icon-button
          class="action-button action-button--info"
          [appPermission]="copyPermission"
          [disabled]="isLocked"
          [ngClass]="{'permission-disabled': isLocked}"
          (click)="onCopy()"
          [attr.aria-label]="'Copy task'"
          matTooltip="Copy Task">
          <mat-icon>content_copy</mat-icon>
        </button>
      }
      <!-- View Mode: PDF Export Button -->
      @if (showPdfExport) {
        <button
          mat-icon-button
          class="action-button action-button--info"
          [appPermission]="pdfExportPermission"
          (click)="onPdfExport()"
          [attr.aria-label]="'Export PDF'"
          matTooltip="Export PDF">
          <mat-icon>description</mat-icon>
        </button>
      }
    </div>
  }

  <!-- Divider after view mode actions -->
  @if (isViewMode && (showStatusDropdown || showMarkComplete || showPdfExport || showCopy)) {
    <div class="action-divider"></div>
  }

  <!-- GROUP 4: Primary Actions (Edit) -->
  @if (isViewMode) {
    <div class="action-group action-group--primary">
      <!-- View Mode: Edit Button -->
      <button
        mat-raised-button
        class="save-button"
        (click)="onEdit()"
        [appPermission]="editPermission"
        [disabled]="isLocked"
        [ngClass]="{'permission-disabled': isLocked}"
        [attr.aria-label]="'Edit task'"
        matTooltip="Edit task">
        <mat-icon>edit_note</mat-icon>
        <span class="button-text">Edit</span>
      </button>
    </div>
  }

  <!-- Divider before system actions -->
  @if (isViewMode) {
    <div class="action-divider"></div>
  }

  <!-- GROUP 5: System Actions (Close, Delete) -->
  <div class="action-group action-group--system">
    <!-- Delete Button (Edit Mode or View Mode with Delete Permission) -->
    @if ((isEdit || showDelete) && !isViewMode) {
      <button
        mat-icon-button
        class="action-button action-button--danger"
        [appPermission]="deletePermission"
        (click)="onDeleteClick()"
        [disabled]="isLocked"
        [ngClass]="{'permission-disabled': isLocked}"
        [attr.aria-label]="'Delete task'"
        matTooltip="Delete task">
        <mat-icon>delete_forever</mat-icon>
      </button>
    }
    <!-- Close Button -->
    <button
      mat-icon-button
      class="action-button"
      (click)="onCloseClick()"
      [attr.aria-label]="'Close'"
      matTooltip="Close">
      <mat-icon>close</mat-icon>
    </button>
  </div>

</div>

</div>

<!-- Animated Search Bar -->
@if (isSearchOpen) {
  <div class="search-bar-container" [@slideDown]>
    <div class="search-input-wrapper">
      <mat-icon class="search-icon">search</mat-icon>
      <input
        #searchInput
        type="text"
        class="search-input"
        placeholder="Search within this task..."
        [value]="searchQuery"
        (input)="onSearchInput($event)"
        [attr.aria-label]="'Search within task'">
    </div>
  </div>
}
